import { useEffect } from "react";

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;1,400&family=DM+Sans:wght@300;400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --navy: #0A1628;
    --blue: #1B6CA8;
    --teal: #0EA5C9;
    --orange: #F4845F;
    --green: #1A7A4A;
    --white: #FFFFFF;
    --offwhite: #F7F9FC;
    --text: #1A2B3C;
    --muted: #64748B;
    --border: rgba(0,0,0,0.08);
  }

  body { font-family: 'DM Sans', sans-serif; background: var(--offwhite); color: var(--text); }

  /* ── Hero ── */
  .ph-hero {
    background: var(--navy);
    position: relative;
    overflow: hidden;
    padding: 80px 48px 72px;
  }
  .ph-hero::before {
    content: '';
    position: absolute;
    top: -100px; right: -100px;
    width: 600px; height: 600px;
    background: radial-gradient(circle, rgba(27,108,168,0.25) 0%, transparent 70%);
    pointer-events: none;
  }
  .ph-hero::after {
    content: '';
    position: absolute;
    bottom: -80px; left: 10%;
    width: 400px; height: 400px;
    background: radial-gradient(circle, rgba(244,132,95,0.1) 0%, transparent 70%);
    pointer-events: none;
  }
  .ph-hero-inner { max-width: 960px; margin: 0 auto; position: relative; z-index: 1; }
  .ph-eyebrow {
    display: inline-flex; align-items: center; gap: 8px;
    background: rgba(255,255,255,0.07);
    border: 1px solid rgba(255,255,255,0.12);
    border-radius: 20px; padding: 6px 16px;
    font-size: 11px; font-weight: 500;
    color: rgba(255,255,255,0.55);
    letter-spacing: 0.12em; text-transform: uppercase;
    margin-bottom: 32px;
  }
  .ph-eyebrow-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--teal); }
  .ph-hero-title {
    font-family: 'Playfair Display', serif;
    font-size: clamp(2.4rem, 5vw, 4rem);
    font-weight: 700; color: var(--white);
    line-height: 1.06; letter-spacing: -0.02em;
    margin-bottom: 10px;
  }
  .ph-hero-title span { color: var(--orange); }
  .ph-hero-sub {
    font-family: 'Playfair Display', serif;
    font-size: clamp(1rem, 2vw, 1.4rem);
    font-style: italic; font-weight: 400;
    color: rgba(255,255,255,0.4);
    margin-bottom: 28px;
  }
  .ph-hero-desc {
    font-size: 1rem; font-weight: 300;
    color: rgba(255,255,255,0.58);
    line-height: 1.8; max-width: 620px;
    margin-bottom: 44px;
  }
  .ph-hero-cta {
    display: inline-flex; align-items: center; gap: 8px;
    background: var(--orange); color: var(--white);
    text-decoration: none; font-size: 0.92rem;
    font-weight: 500; padding: 14px 32px;
    border-radius: 4px; transition: all 0.2s;
  }
  .ph-hero-cta:hover { background: #e06b44; transform: translateY(-1px); }

  /* ── Problem bar ── */
  .ph-problem {
    background: #0D1F35;
    padding: 48px;
    border-top: 1px solid rgba(255,255,255,0.06);
  }
  .ph-problem-inner { max-width: 960px; margin: 0 auto; }
  .ph-problem-label {
    font-size: 0.65rem; font-weight: 600;
    letter-spacing: 0.2em; text-transform: uppercase;
    color: var(--orange); margin-bottom: 20px;
  }
  .ph-problem-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 32px;
  }
  .ph-problem-item { }
  .ph-problem-stat {
    font-family: 'Playfair Display', serif;
    font-size: 2.8rem; font-weight: 700;
    color: var(--white); line-height: 1;
    margin-bottom: 8px;
  }
  .ph-problem-stat span { color: var(--orange); }
  .ph-problem-text {
    font-size: 0.85rem; font-weight: 300;
    color: rgba(255,255,255,0.55); line-height: 1.65;
  }

  /* ── Main ── */
  .ph-main { max-width: 960px; margin: 0 auto; padding: 72px 48px; }

  .ph-section-label {
    font-size: 0.65rem; font-weight: 600;
    letter-spacing: 0.2em; text-transform: uppercase;
    color: var(--muted); margin-bottom: 12px;
  }
  .ph-section-title {
    font-family: 'Playfair Display', serif;
    font-size: clamp(1.6rem, 3vw, 2.2rem);
    font-weight: 700; color: var(--navy);
    line-height: 1.2; margin-bottom: 14px;
  }
  .ph-section-body {
    font-size: 0.95rem; font-weight: 300;
    color: var(--muted); line-height: 1.8;
    max-width: 640px; margin-bottom: 40px;
  }

  /* ── Pain points ── */
  .ph-pain-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 20px; margin-bottom: 72px;
  }
  .ph-pain-card {
    background: var(--white);
    border: 1px solid var(--border);
    border-radius: 8px; padding: 32px 24px;
    border-top: 3px solid var(--blue);
    transition: transform 0.2s, box-shadow 0.2s;
  }
  .ph-pain-card:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(0,0,0,0.08); }
  .ph-pain-card.orange { border-top-color: var(--orange); }
  .ph-pain-card.green { border-top-color: var(--green); }
  .ph-pain-icon { font-size: 28px; margin-bottom: 16px; }
  .ph-pain-title { font-size: 1rem; font-weight: 600; color: var(--navy); margin-bottom: 10px; }
  .ph-pain-body { font-size: 0.82rem; font-weight: 300; color: var(--muted); line-height: 1.7; margin-bottom: 16px; }
  .ph-pain-solution {
    font-size: 0.78rem; font-weight: 500;
    color: var(--blue); line-height: 1.6;
    padding-top: 16px;
    border-top: 1px solid var(--border);
  }
  .ph-pain-solution.orange { color: var(--orange); }
  .ph-pain-solution.green { color: var(--green); }

  /* ── How it works ── */
  .ph-how {
    background: var(--navy);
    border-radius: 12px; padding: 56px 48px;
    margin-bottom: 72px;
  }
  .ph-how-title {
    font-family: 'Playfair Display', serif;
    font-size: 1.8rem; font-weight: 700;
    color: var(--white); margin-bottom: 10px;
  }
  .ph-how-sub {
    font-size: 0.88rem; font-weight: 300;
    color: rgba(255,255,255,0.5);
    margin-bottom: 40px; line-height: 1.7;
    max-width: 560px;
  }
  .ph-how-steps {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 0; position: relative;
  }
  .ph-how-steps::before {
    content: '';
    position: absolute;
    top: 20px; left: 10%;
    width: 80%; height: 1px;
    background: rgba(255,255,255,0.1);
  }
  .ph-how-step { text-align: center; padding: 0 12px; }
  .ph-how-num {
    width: 40px; height: 40px; border-radius: 50%;
    background: var(--blue); color: white;
    font-size: 0.9rem; font-weight: 600;
    display: flex; align-items: center;
    justify-content: center; margin: 0 auto 16px;
    position: relative; z-index: 1;
  }
  .ph-how-step-title {
    font-size: 0.85rem; font-weight: 600;
    color: var(--white); margin-bottom: 8px;
  }
  .ph-how-step-body {
    font-size: 0.76rem; font-weight: 300;
    color: rgba(255,255,255,0.45); line-height: 1.6;
  }

  /* ── Products ── */
  .ph-products {
    display: grid;
    grid-template-columns: 1fr;
    max-width: 560px;
    margin: 0 auto 72px;
    gap: 24px;
  }
  .ph-product {
    background: var(--white);
    border: 1px solid var(--border);
    border-radius: 8px; overflow: hidden;
  }
  .ph-product-head {
    padding: 28px; background: var(--navy);
  }
  .ph-product-head.teal { background: #0A2438; }
  .ph-product-icon { font-size: 22px; margin-bottom: 10px; display: block; }
  .ph-product-name {
    font-family: 'Playfair Display', serif;
    font-size: 1.4rem; font-weight: 700;
    color: var(--white); margin-bottom: 4px;
  }
  .ph-product-name span { color: var(--orange); }
  .ph-product-tagline {
    font-size: 0.75rem; color: rgba(255,255,255,0.4);
    font-style: italic;
  }
  .ph-product-body { padding: 24px 28px; }
  .ph-product-desc {
    font-size: 0.85rem; font-weight: 300;
    color: var(--muted); line-height: 1.7;
    margin-bottom: 20px;
  }
  .ph-product-features { list-style: none; }
  .ph-product-feature {
    display: flex; gap: 10px; align-items: flex-start;
    font-size: 0.8rem; color: var(--text);
    line-height: 1.5; margin-bottom: 10px;
  }
  .ph-product-feature-dot {
    width: 16px; height: 16px; border-radius: 50%;
    background: var(--blue); color: white;
    font-size: 9px; display: flex;
    align-items: center; justify-content: center;
    flex-shrink: 0; margin-top: 1px;
  }
  .ph-product-feature-dot.orange { background: var(--orange); }

  /* ── Partnership models ── */
  .ph-models-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 20px; margin-bottom: 72px;
  }
  .ph-model {
    background: var(--white);
    border: 1px solid var(--border);
    border-radius: 8px; padding: 32px 24px;
  }
  .ph-model-num {
    font-family: 'Playfair Display', serif;
    font-size: 2.4rem; font-weight: 700;
    color: rgba(27,108,168,0.15);
    line-height: 1; margin-bottom: 12px;
  }
  .ph-model-title {
    font-size: 0.95rem; font-weight: 600;
    color: var(--navy); margin-bottom: 10px;
  }
  .ph-model-body {
    font-size: 0.8rem; font-weight: 300;
    color: var(--muted); line-height: 1.7;
    margin-bottom: 16px;
  }
  .ph-model-tag {
    display: inline-block;
    background: rgba(27,108,168,0.08);
    color: var(--blue);
    font-size: 0.68rem; font-weight: 600;
    letter-spacing: 0.08em; text-transform: uppercase;
    padding: 4px 12px; border-radius: 20px;
  }

  /* ── Why us ── */
  .ph-why-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px; margin-bottom: 72px;
  }
  .ph-why-item {
    display: flex; gap: 16px; align-items: flex-start;
    background: var(--white);
    border: 1px solid var(--border);
    border-radius: 8px; padding: 24px;
  }
  .ph-why-icon {
    font-size: 22px; flex-shrink: 0;
    width: 44px; height: 44px;
    background: rgba(27,108,168,0.06);
    border-radius: 8px;
    display: flex; align-items: center; justify-content: center;
  }
  .ph-why-title {
    font-size: 0.9rem; font-weight: 600;
    color: var(--navy); margin-bottom: 6px;
  }
  .ph-why-body {
    font-size: 0.8rem; font-weight: 300;
    color: var(--muted); line-height: 1.65;
  }

  /* ── CTA ── */
  .ph-cta {
    background: linear-gradient(135deg, #1B6CA8, #0A4A7A);
    border-radius: 12px; padding: 56px 48px;
    text-align: center; margin-bottom: 48px;
  }
  .ph-cta-title {
    font-family: 'Playfair Display', serif;
    font-size: clamp(1.6rem, 3vw, 2.2rem);
    font-weight: 700; color: var(--white);
    margin-bottom: 14px;
  }
  .ph-cta-body {
    font-size: 0.95rem; font-weight: 300;
    color: rgba(255,255,255,0.6); line-height: 1.8;
    margin-bottom: 32px; max-width: 520px;
    margin-left: auto; margin-right: auto;
  }
  .ph-cta-buttons { display: flex; gap: 16px; justify-content: center; flex-wrap: wrap; }
  .ph-cta-btn-primary {
    display: inline-flex; align-items: center; gap: 8px;
    background: var(--orange); color: var(--white);
    text-decoration: none; font-size: 0.92rem;
    font-weight: 500; padding: 14px 32px;
    border-radius: 4px; transition: all 0.2s;
  }
  .ph-cta-btn-primary:hover { background: #e06b44; transform: translateY(-1px); }
  .ph-cta-btn-secondary {
    display: inline-flex; align-items: center; gap: 8px;
    background: transparent; color: rgba(255,255,255,0.7);
    text-decoration: none; font-size: 0.92rem;
    font-weight: 400; padding: 14px 32px;
    border: 1px solid rgba(255,255,255,0.25);
    border-radius: 4px; transition: all 0.2s;
  }
  .ph-cta-btn-secondary:hover { border-color: rgba(255,255,255,0.6); color: var(--white); }

  /* ── Footer ── */
  .ph-footer { text-align: center; padding: 0 0 56px; }
  .ph-footer-logo {
    font-family: 'Playfair Display', serif;
    font-size: 1.1rem; font-weight: 700;
    color: var(--navy); margin-bottom: 8px;
  }
  .ph-footer-logo span { color: var(--orange); }
  .ph-footer-text { font-size: 0.75rem; color: var(--muted); line-height: 1.6; }
  .ph-footer-links { display: flex; gap: 24px; justify-content: center; margin-top: 12px; }
  .ph-footer-links a { font-size: 0.75rem; color: var(--blue); text-decoration: none; }
  .ph-footer-links a:hover { text-decoration: underline; }

  @media (max-width: 720px) {
    .ph-hero, .ph-main { padding: 48px 24px; }
    .ph-problem { padding: 40px 24px; }
    .ph-problem-grid { grid-template-columns: 1fr; gap: 24px; }
    .ph-pain-grid { grid-template-columns: 1fr; }
    .ph-how { padding: 40px 24px; }
    .ph-how-steps { grid-template-columns: 1fr 1fr; gap: 24px; }
    .ph-how-steps::before { display: none; }
    .ph-products { grid-template-columns: 1fr; }
    .ph-models-grid { grid-template-columns: 1fr; }
    .ph-why-grid { grid-template-columns: 1fr; }
    .ph-cta { padding: 40px 24px; }
  }
`;

export default function PharmaPage() {
  useEffect(() => {
    document.title = "Pharma Partnerships · VeraInsight Healthcare";
  }, []);

  return (
    <>
      <style>{css}</style>

      {/* Hero */}
      <section className="ph-hero">
        <div className="ph-hero-inner">
          <div className="ph-eyebrow">
            <div className="ph-eyebrow-dot"></div>
            VeraInsight Healthcare · Pharma Partnerships
          </div>
          <h1 className="ph-hero-title">
            Reach more patients.<br/>
            Retain them <span>longer.</span>
          </h1>
          <p className="ph-hero-sub">
            Patient recruitment · Retention · Education
          </p>
          <p className="ph-hero-desc">
            VeraInsight Healthcare operates AmyloidConnect — a platform that reaches
            amyloidosis patients at the moment they are most engaged: searching for
            a diagnosis, exploring trials, and preparing to travel to a site, all
            within a single integrated flow. We partner with pharma companies to
            close the gap between patients and the trials that need them.
          </p>
          <a href="mailto:sandeep@verainsight.com" className="ph-hero-cta">
            Start a conversation →
          </a>
        </div>
      </section>

      {/* Problem bar */}
      <div className="ph-problem">
        <div className="ph-problem-inner">
          <div className="ph-problem-label">The problem we solve together</div>
          <div className="ph-problem-grid">
            <div className="ph-problem-item">
              <div className="ph-problem-stat"><span>30%</span></div>
              <div className="ph-problem-text">
                of clinical trial patients drop out due to travel burden —
                not because of the treatment. Dropout directly impacts your
                trial timeline and statistical power.
              </div>
            </div>
            <div className="ph-problem-item">
              <div className="ph-problem-stat"><span>10y</span></div>
              <div className="ph-problem-text">
                average diagnostic delay for ATTR-v amyloidosis. Patients
                are in your target population years before they are diagnosed —
                education changes that.
              </div>
            </div>
            <div className="ph-problem-item">
              <div className="ph-problem-stat"><span>60%</span></div>
              <div className="ph-problem-text">
                of trials requiring patient travel have no dedicated
                coordination support. AmyloidConnect's built-in travel booking
                fills that gap — reducing dropout and protecting enrollment numbers.
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <main className="ph-main">

        {/* Pain points */}
        <p className="ph-section-label">Where we create value</p>
        <h2 className="ph-section-title">Three problems. One platform. One partner.</h2>
        <p className="ph-section-body">
          AmyloidConnect addresses the three biggest operational challenges
          pharma faces in amyloidosis trials — across recruitment, retention,
          and patient education — in a single integrated platform.
        </p>

        <div className="ph-pain-grid">
          <div className="ph-pain-card">
            <div className="ph-pain-icon">🎯</div>
            <div className="ph-pain-title">Patient Recruitment</div>
            <div className="ph-pain-body">
              Finding amyloidosis patients who are trial-eligible, engaged,
              and ready to act is expensive and slow. Traditional recruitment
              relies on site referrals and paid ads targeting a population
              that doesn't know it has the disease.
            </div>
            <div className="ph-pain-solution">
              AmyloidConnect reaches patients actively seeking information
              about their diagnosis — the highest-intent moment in the
              patient journey. Trial matching surfaces your study directly
              to eligible patients.
            </div>
          </div>
          <div className="ph-pain-card orange">
            <div className="ph-pain-icon">🔄</div>
            <div className="ph-pain-title">Patient Retention</div>
            <div className="ph-pain-body">
              30% of enrolled patients drop out due to travel burden.
              Every dropout costs your trial time, money, and statistical
              power — and may require re-enrollment to maintain sample size.
            </div>
            <div className="ph-pain-solution orange">
              AmyloidConnect's built-in travel booking eliminates the travel
              barrier. Patients book flights, hotels, and ground transport
              directly from the trial card — coordinated around their visit
              schedule. Fewer logistical barriers means fewer dropouts.
            </div>
          </div>
          <div className="ph-pain-card green">
            <div className="ph-pain-icon">📚</div>
            <div className="ph-pain-title">Patient Education</div>
            <div className="ph-pain-body">
              Uninformed patients ask more questions, require more
              coordinator time, and are less likely to remain compliant
              with protocol requirements. Education at scale is expensive
              to build internally.
            </div>
            <div className="ph-pain-solution green">
              AmyloidConnect provides clinically accurate, patient-accessible
              education on every organ manifestation, biomarker staging,
              and treatment landscape — reducing the educational burden
              on your site coordinators.
            </div>
          </div>
        </div>

        {/* How it works */}
        <div className="ph-how">
          <h2 className="ph-how-title">How the partnership works</h2>
          <p className="ph-how-sub">
            From first diagnosis to trial enrollment to travel coordination —
            AmyloidConnect supports the patient at every step, in one
            continuous flow rather than a hand-off between separate tools.
          </p>
          <div className="ph-how-steps">
            {[
              { num: "1", title: "Patient discovers AmyloidConnect", body: "Through search, physician referral, or advocacy org listing. They read about their organ involvement and staging." },
              { num: "2", title: "Trial matching surfaces your study", body: "AI-powered matching shows eligible patients your trial based on amyloid type, organ involvement, and location." },
              { num: "3", title: "Patient books travel from the trial card", body: "Flight and hotel booking pre-filled with trial context and site location — no separate platform, no re-entering details." },
              { num: "4", title: "Travel confirmed, patient arrives", body: "Itinerary emailed automatically. Patient shows up." },
            ].map((s) => (
              <div className="ph-how-step" key={s.num}>
                <div className="ph-how-num">{s.num}</div>
                <div className="ph-how-step-title">{s.title}</div>
                <div className="ph-how-step-body">{s.body}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Product */}
        <p className="ph-section-label">The platform</p>
        <h2 className="ph-section-title">One product, one integrated pipeline</h2>
        <p className="ph-section-body">
          Education, trial matching, and travel booking are built into a single
          platform — a patient can go from reading about their diagnosis to
          booking travel for a trial in one session, with no hand-off between
          separate tools.
        </p>
        <div className="ph-products">
          <div className="ph-product">
            <div className="ph-product-head">
              <span className="ph-product-icon">🔬</span>
              <div className="ph-product-name">Amyloid<span>Connect</span></div>
              <div className="ph-product-tagline">Patient education, clinical trial matching, and travel booking</div>
            </div>
            <div className="ph-product-body">
              <p className="ph-product-desc">
                A comprehensive platform for amyloidosis patients covering
                every organ system, biomarker staging, active clinical
                trials, and travel coordination for trial visits — built
                to engage patients at the earliest point in their diagnostic
                journey and support them through to arrival at a trial site.
              </p>
              <ul className="ph-product-features">
                {[
                  "6 organ-specific education modules — cardiac, neuro, renal, GI, MSK, ophthalmic",
                  "Biomarker staging calculators — Mayo 2004, Mayo 2012, European",
                  "AI-powered trial matching — AL, ATTR-v, ATTR-wt",
                  "Integrated travel booking — flights, hotels, and ground transport, pre-filled with trial context",
                  "HIPAA compliant · SOC 2 Type II infrastructure",
                  "Patient and researcher roles — separate access levels",
                ].map((f) => (
                  <li className="ph-product-feature" key={f}>
                    <span className="ph-product-feature-dot">✓</span>
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Partnership models */}
        <p className="ph-section-label">Partnership models</p>
        <h2 className="ph-section-title">Flexible structures — we work with you</h2>
        <p className="ph-section-body">
          We are open to exploring partnership structures that create value
          for your organization. Below are three starting points — the right
          model depends on your goals and trial stage.
        </p>
        <div className="ph-models-grid">
          {[
            {
              num: "01",
              title: "Trial Visibility Partnership",
              body: "Feature your active amyloidosis trials on AmyloidConnect's trial matching platform. Patients searching for trials matching their profile see your study first. Ideal for enrollment-stage trials needing to reach a pre-qualified, engaged patient population.",
              tag: "Recruitment focus",
            },
            {
              num: "02",
              title: "Patient Travel Program",
              body: "Feature your trial's patient support program alongside AmyloidConnect's built-in travel booking. Patients enrolled in your trial book flights and hotels directly from the platform — reducing dropout, simplifying logistics, and improving the patient experience at your sites.",
              tag: "Retention focus",
            },
            {
              num: "03",
              title: "Full Platform Partnership",
              body: "End-to-end patient support — from first education touchpoint through trial enrollment and travel coordination, all within one platform. Co-branded experience available. Suitable for sponsors looking for a comprehensive patient engagement solution.",
              tag: "Full pipeline",
            },
          ].map((m) => (
            <div className="ph-model" key={m.num}>
              <div className="ph-model-num">{m.num}</div>
              <div className="ph-model-title">{m.title}</div>
              <div className="ph-model-body">{m.body}</div>
              <div className="ph-model-tag">{m.tag}</div>
            </div>
          ))}
        </div>

        {/* Why us */}
        <p className="ph-section-label">Why VeraInsight</p>
        <h2 className="ph-section-title">What makes us different</h2>
        <p className="ph-section-body">
          We are not a generic patient recruitment vendor or a travel management
          company. We are disease-specific, deeply integrated, and built by
          people who understand the amyloidosis patient journey.
        </p>
        <div className="ph-why-grid">
          {[
            { icon: "🎯", title: "Disease-specific, not generic", body: "Every feature of AmyloidConnect is built for amyloidosis — not adapted from a generic template. Patients recognize the difference and trust it." },
            { icon: "🔗", title: "End-to-end integration", body: "No other platform connects patient education → trial matching → travel booking in a single integrated flow. There's no hand-off from interest to enrollment to arrival — it's one product." },
            { icon: "🏥", title: "Physician-inspired and validated", body: "AmyloidConnect was conceived with a renowned amyloidosis specialist. The clinical content is accurate, peer-informed, and patient-accessible." },
            { icon: "🔒", title: "HIPAA compliant infrastructure", body: "Built on AWS with HIPAA-eligible services, SOC 2 Type II architecture, and full data security practices appropriate for healthcare partnerships." },
            { icon: "⚡", title: "Live and production-ready", body: "AmyloidConnect is live in production — not a proposal or a prototype. You can see it working at amyloidconnect.com today." },
            { icon: "🤝", title: "Flexible and founder-led", body: "You work directly with the founder — no account managers, no red tape. We move fast, customize quickly, and are genuinely invested in outcomes." },
          ].map((w) => (
            <div className="ph-why-item" key={w.title}>
              <div className="ph-why-icon">{w.icon}</div>
              <div>
                <div className="ph-why-title">{w.title}</div>
                <div className="ph-why-body">{w.body}</div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="ph-cta">
          <h2 className="ph-cta-title">Let's start a conversation</h2>
          <p className="ph-cta-body">
            We are actively seeking pharma partners with active amyloidosis
            programs. Whether you are in enrollment, retention, or patient
            education — there is likely a way we can create value together.
            No commitment required for an initial call.
          </p>
          <div className="ph-cta-buttons">
            <a href="mailto:sandeep@verainsight.com?subject=Pharma Partnership Inquiry — VeraInsight" className="ph-cta-btn-primary">
              Email us directly
            </a>
            <a href="https://amyloidconnect.com" target="_blank" rel="noopener noreferrer" className="ph-cta-btn-secondary">
              See AmyloidConnect →
            </a>
          </div>
          <p style={{ marginTop: 24, fontSize: "0.78rem", color: "rgba(255,255,255,0.35)" }}>
            sandeep@verainsight.com · verainsight.com · Chicago, IL
          </p>
        </div>

        {/* Footer */}
        <div className="ph-footer">
          <div className="ph-footer-logo">Vera<span>Insight</span> Healthcare Inc.</div>
          <div className="ph-footer-text">
            Building technology for rare disease patients · Chicago, IL
          </div>
          <div className="ph-footer-links">
            <a href="https://amyloidconnect.com" target="_blank" rel="noopener noreferrer">AmyloidConnect</a>
            <a href="/platforms">Platforms</a>
            <a href="mailto:sandeep@verainsight.com">Contact</a>
          </div>
        </div>

      </main>
    </>
  );
}